import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from './utils/api';

function SettingsNumbers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialModule = searchParams.get('module') || 'quotes';
  
  const [selectedModule, setSelectedModule] = useState(initialModule);
  const [format, setFormat] = useState({
    prefix: '',
    padding: 8,
    suffix: '',
    next_number: 1
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const modules = [
    { id: 'quotes', label: 'Quote Numbers' },
    { id: 'invoices', label: 'Invoice Numbers' },
    { id: 'proformas', label: 'Proforma Numbers' },
    { id: 'jobs', label: 'Job Numbers' },
    { id: 'ppm_quotes', label: 'PPM Quote Numbers' },
    { id: 'ppm_contracts', label: 'PPM Contract Numbers' },
    { id: 'equipment', label: 'Equipment Numbers' },
    { id: 'customers', label: 'Customer Numbers' },
    { id: 'sites', label: 'Site Numbers' },
  ];

  useEffect(() => {
    fetchFormat(selectedModule);
  }, [selectedModule]);

  const fetchFormat = async (module) => {
    setLoading(true);
    try {
      const response = await apiFetch(`/api/number-formats/${module}`);
      const data = await response.json();
      setFormat(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching format:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(`/api/number-formats/${selectedModule}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(format)
      });
      setSaving(false);
    } catch (error) {
      console.error('Error saving format:', error);
      setSaving(false);
      alert('Failed to save');
    }
  };

  const generatePreview = () => {
    const paddedNumber = String(format.next_number).padStart(format.padding, '0');
    return `${format.prefix}${paddedNumber}${format.suffix}`;
  };

  const currentModule = modules.find(m => m.id === selectedModule);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Number Formats</h2>
            <p className="text-sm text-gray-600 mt-1">Configure number formats for each module</p>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Left Sidebar - Module Menu */}
        <div className="w-64 border-r border-gray-200 p-4">
          <div className="text-xs font-semibold text-gray-500 mb-2 px-2">Select Module</div>
          <nav className="space-y-1">
            {modules.map(module => (
              <button
                key={module.id}
                onClick={() => setSelectedModule(module.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                  selectedModule === module.id
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {module.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Right Content - Format Settings */}
        <div className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="max-w-2xl">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{currentModule?.label}</h3>

              <div className="space-y-6">
                {/* Preview */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Next Number Preview:</div>
                  <div className="text-3xl font-bold text-blue-700 font-mono">{generatePreview()}</div>
                </div>

                {/* Prefix */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prefix <span className="text-gray-400 font-normal">(optional, 0-4 characters)</span>
                  </label>
                  <input
                    type="text"
                    value={format.prefix}
                    onChange={(e) => setFormat({ ...format, prefix: e.target.value })}
                    onBlur={handleSave}
                    maxLength={4}
                    placeholder="e.g. Q-, JOB-"
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Text before the number</p>
                </div>

                {/* Padding */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number Length <span className="text-gray-400 font-normal">(digits, 1-8)</span>
                  </label>
                  <input
                    type="number"
                    value={format.padding}
                    onChange={(e) => setFormat({ ...format, padding: parseInt(e.target.value) })}
                    onBlur={handleSave}
                    min={1}
                    max={8}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of digits (will be zero-padded)</p>
                </div>

                {/* Suffix */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Suffix <span className="text-gray-400 font-normal">(optional, 0-4 characters)</span>
                  </label>
                  <input
                    type="text"
                    value={format.suffix}
                    onChange={(e) => setFormat({ ...format, suffix: e.target.value })}
                    onBlur={handleSave}
                    maxLength={4}
                    placeholder="e.g. -UK, -2026"
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Text after the number</p>
                </div>

                {/* Next Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Next Number
                  </label>
                  <input
                    type="number"
                    value={format.next_number}
                    onChange={(e) => setFormat({ ...format, next_number: parseInt(e.target.value) })}
                    onBlur={handleSave}
                    min={1}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">The next sequential number to use</p>
                </div>

                {/* Save Status */}
                {saving && (
                  <div className="text-sm text-blue-600">Saving...</div>
                )}

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">ℹ️ How it works</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Changes save automatically when you finish typing</li>
                    <li>• Standard format: Leave prefix and suffix empty for just numbers (00000001)</li>
                    <li>• Each new record will auto-increment the number</li>
                    <li>• You can manually adjust the next number if needed</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsNumbers;
